import { Package } from 'lucide-react'
import pluralize from 'pluralize'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatDate } from '@/lib/formatters'
import { type ConfirmedLargeFleetPacket, useCustomerLargeFleetPackets } from '@/models/largeFleetPacket'
import { TRAVEL_RANGE_OPTIONS } from '../../../Public/LargeFleet/packetData'

function travelRangeNames(values: string[]): string {
  const names = values.map(
    (value) => TRAVEL_RANGE_OPTIONS.find((option) => option.value === value)?.name ?? value
  )
  return names.length ? names.join(', ') : '—'
}

function PacketDetails({ packet }: { packet: ConfirmedLargeFleetPacket }) {
  return (
    <div className="space-y-2">
      <div>
        <span className="text-muted-foreground">Sent by:</span> {packet.fullName} ({packet.email})
      </div>
      <div>
        <span className="text-muted-foreground">Confirmed:</span> {formatDate(packet.confirmedAt)}
      </div>
      <div>
        <span className="text-muted-foreground">Travel range:</span> {travelRangeNames(packet.travelRange)}
      </div>
      <div>
        <span className="text-muted-foreground">Files:</span> {pluralize('file', packet.fileCount, true)}
      </div>
      <div>
        <span className="text-muted-foreground">Group captive:</span>{' '}
        {packet.wantsGroupCaptive ? (
          <Badge className="align-middle whitespace-normal">Wants to hear about group captive options</Badge>
        ) : (
          'Not requested'
        )}
      </div>
    </div>
  )
}

/** The large-fleet packets this customer sent; renders nothing for a customer who never sent one. */
export function LargeFleetPacketCard({ orgId }: { orgId: string }) {
  const { data: packets } = useCustomerLargeFleetPackets(orgId)
  if (!packets?.length) return null

  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Package className="size-4" />
          {pluralize('Large fleet packet', packets.length)}
        </CardTitle>
      </CardHeader>
      <CardContent className="divide-y text-sm [&>*]:py-3 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
        {packets.map((packet) => (
          <PacketDetails key={packet.id} packet={packet} />
        ))}
      </CardContent>
    </Card>
  )
}
